<template>
  <div>
    <div class="flex items-center text-center text-gray-900">
      <button
        type="button"
        class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
        @click="previousWeek"
      >
        <span class="sr-only">Previous week</span>
        <svg
          class="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fill-rule="evenodd"
            d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
            clip-rule="evenodd"
          />
        </svg>
      </button>
      <div class="flex-auto text-lg leading-6 font-semibold">
        <span v-if="person">{{ person.name }} / </span>
        {{ weekRange }}
        <span
          v-if="isLoading"
          class="absolute dib ml-3"
        >
          <svg
            class="animate-spin h-5 w-5 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="white"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="#999"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </span>
      </div>
      <button
        type="button"
        class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
        @click="nextWeek"
      >
        <span class="sr-only">Next week</span>
        <svg
          class="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fill-rule="evenodd"
            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
            clip-rule="evenodd"
          />
        </svg>
      </button>
    </div>

    <!-- Week day headers -->
    <div class="mt-6 grid gap-px bg-slate-200 text-center text-xs leading-6 text-gray-500 rounded-t-lg" :style="gridStyle">
      <!-- Empty corner for time column -->
      <div v-if="hasBusinessHours" class="bg-white"></div>
      <div
        v-for="day in weekDays"
        :key="`header-${day.date}`"
        class="text-center font-medium py-2 bg-white"
      >
        {{ day.dayName }}
        <div class="text-lg" :class="{ 'text-sky-600 font-bold': day.isToday }">{{ day.dayNumber }}</div>
      </div>
    </div>

    <!-- Full-day events row (only when business hours are set) -->
    <div v-if="hasBusinessHours && hasFullDayEvents" class="grid gap-px bg-slate-200" :style="gridStyle">
      <div v-if="hasBusinessHours" class="bg-white py-1 px-1 text-xs text-gray-500">
        All day
      </div>
      <!-- eslint-disable vue/no-unused-vars -- The `index` binding is dead; v-for does not
           need it. Dropping it recompiles this template, so it is a follow-up. -->
      <div
        v-for="(day, index) in weekDays"
        :key="`fullday-${day.date}`"
        class="bg-white py-1 px-1 min-h-[40px]"
      >
        <div
          v-for="event in fullDayEventsForDate(day.dateObj)"
          :key="`fullday-event-${event.id}`"
          class="text-xs mb-1"
        >
          <a
            :href="eventUrl(event)"
            class="block rounded px-1 py-0.5 bg-sky-100 hover:bg-sky-200 text-gray-700 truncate"
          >
            {{ event.name }}
          </a>
        </div>
      </div>
      <!-- eslint-enable vue/no-unused-vars -->
    </div>

    <!-- Hourly grid (when business hours are set) -->
    <div v-if="hasBusinessHours" class="grid gap-px bg-slate-200 text-sm rounded-b-lg" :style="gridStyle">
      <!-- Time slots -->
      <template v-for="hour in hourSlots" :key="`hour-row-${hour}`">
        <!-- Hour label -->
        <div class="bg-gray-50 py-2 px-1 text-xs text-gray-500 text-right pr-2 border-t border-gray-100">
          {{ formatHour(hour) }}
        </div>
        <!-- Day columns for this hour -->
        <!-- eslint-disable vue/no-unused-vars -- The `dayIndex` binding is dead; v-for does not
             need it. Dropping it recompiles this template, so it is a follow-up. -->
        <div
          v-for="(day, dayIndex) in weekDays"
          :key="`hour-${hour}-day-${day.date}`"
          class="bg-white relative border-t border-gray-100"
          :class="{ 'ring-2 ring-sky-600 ring-inset': day.isToday }"
          style="min-height: 40px;"
        >
          <!-- Hourly events that start in this hour slot -->
          <div
            v-for="event in hourlyEventsForDateAndHour(day.dateObj, hour)"
            :key="`hourly-event-${event.id}`"
            class="absolute left-0 right-0 mx-0.5 z-10"
            :style="getEventStyle(event, hour)"
          >
            <a
              :href="eventUrl(event)"
              class="block rounded px-1 py-0.5 bg-orange-100 hover:bg-orange-200 text-gray-700 text-xs truncate h-full"
            >
              {{ getEventDisplayName(event) }}
            </a>
          </div>
        </div>
        <!-- eslint-enable vue/no-unused-vars -->
      </template>
    </div>

    <!-- Simple week grid (when no business hours) -->
    <div v-else class="mt-2 grid grid-cols-7 gap-px rounded-lg shadow ring-1 ring-slate-200 bg-slate-200 text-sm min-h-[200px]">
      <div
        v-for="(day, index) in weekDays"
        :key="`day-${day.date}`"
        :class="dayColumnClass(day, index)"
      >
        <ul class="space-y-1 p-1">
          <li
            v-for="event in eventsForDate(day.dateObj)"
            :key="`event-${event.id}`"
            class="text-xs leading-tight"
          >
            <a
              :href="eventUrl(event)"
              class="block rounded px-1 py-1 bg-slate-100/50 hover:bg-white/80 text-gray-700"
            >
              {{ event.name }}
            </a>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
const pad = (n, width, z) => {
  z = z || '0'
  n = n + ''
  return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n
}

const dateStamp = (date) => {
  return [date.getFullYear(), pad(date.getMonth() + 1, 2, 0), pad(date.getDate(), 2, 0)].join('-')
}

const isToday = (date) => {
  const today = new Date()
  return date.getDate() === today.getDate() &&
         date.getMonth() === today.getMonth() &&
         date.getFullYear() === today.getFullYear()
}

const parseTime = (timeStr) => {
  if (!timeStr) return null
  const [hours, minutes] = timeStr.split(':').map(Number)
  return hours + (minutes / 60)
}

export default {
  name: 'weekly-calendar',
  props: {
    initialDate: {
      type: String,
      default () { return dateStamp(new Date()) }
    },
    datesData: {
      type: Object,
      default () { return {} }
    },
    calendarUrl: {
      type: String,
      default: ''
    },
    person: {
      type: Object,
      default: null
    },
    businessHoursStart: {
      type: String,
      default: ''
    },
    businessHoursEnd: {
      type: String,
      default: ''
    }
  },
  data () {
    const splat = this.initialDate.split('-')
    const date = new Date(splat[0], parseInt(splat[1]) - 1, splat[2])

    return {
      currentDate: date,
      isLoading: false
    }
  },
  computed: {
    hasBusinessHours () {
      return this.businessHoursStart && this.businessHoursEnd
    },
    gridStyle () {
      const cols = this.hasBusinessHours ? 8 : 7 // 1 for time column + 7 days
      return {
        gridTemplateColumns: this.hasBusinessHours ? '60px repeat(7, minmax(0, 1fr))' : 'repeat(7, minmax(0, 1fr))'
      }
    },
    startHour () {
      if (!this.businessHoursStart) return 8
      return parseInt(this.businessHoursStart.split(':')[0])
    },
    endHour () {
      if (!this.businessHoursEnd) return 18
      return parseInt(this.businessHoursEnd.split(':')[0])
    },
    hourSlots () {
      const slots = []
      for (let h = this.startHour; h < this.endHour; h++) {
        slots.push(h)
      }
      return slots
    },
    weekStart () {
      const date = new Date(this.currentDate)
      const day = date.getDay()
      date.setDate(date.getDate() - day)
      return date
    },
    weekEnd () {
      const date = new Date(this.weekStart)
      date.setDate(date.getDate() + 6)
      return date
    },
    weekRange () {
      const startMonth = this.weekStart.toLocaleDateString('en-us', { month: 'short' })
      const endMonth = this.weekEnd.toLocaleDateString('en-us', { month: 'short' })
      const startDay = this.weekStart.getDate()
      const endDay = this.weekEnd.getDate()
      const year = this.weekEnd.getFullYear()

      if (startMonth === endMonth) {
        return `${startMonth} ${startDay} - ${endDay}, ${year}`
      }
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`
    },
    weekDays () {
      const days = []
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

      for (let i = 0; i < 7; i++) {
        const date = new Date(this.weekStart)
        date.setDate(date.getDate() + i)
        days.push({
          dateObj: date,
          date: dateStamp(date),
          dayName: dayNames[i],
          dayNumber: date.getDate(),
          isToday: isToday(date)
        })
      }

      return days
    },
    hasFullDayEvents () {
      return this.weekDays.some(day => this.fullDayEventsForDate(day.dateObj).length > 0)
    }
  },
  methods: {
    eventsForDate (date) {
      const data = this.datesData[dateStamp(date)]
      return data && data.events ? data.events : []
    },
    fullDayEventsForDate (date) {
      return this.eventsForDate(date).filter(e => !e.start_hour)
    },
    hourlyEventsForDate (date) {
      return this.eventsForDate(date).filter(e => e.start_hour)
    },
    hourlyEventsForDateAndHour (date, hour) {
      return this.hourlyEventsForDate(date).filter(e => {
        const startHour = parseTime(e.start_hour)
        return Math.floor(startHour) === hour
      })
    },
    formatHour (hour) {
      if (hour === 0) return '12 AM'
      if (hour === 12) return '12 PM'
      if (hour < 12) return `${hour} AM`
      return `${hour - 12} PM`
    },
    getEventStyle (event, slotHour) {
      const startHour = parseTime(event.start_hour)
      const endHour = parseTime(event.end_hour)
      const duration = endHour - startHour

      // Calculate top offset within the slot (for events starting mid-hour)
      const startOffset = (startHour - slotHour) * 40 // 40px per hour slot

      // Calculate height based on duration
      const height = Math.min(duration * 40, (this.endHour - slotHour) * 40)

      return {
        top: `${startOffset}px`,
        height: `${height}px`
      }
    },
    getEventDisplayName (event) {
      // Remove the hours from the name since we're showing it positionally
      const name = event.name
      // The name contains hours like "Name (Category) 9:00 AM - 1:00 PM"
      // Remove the time portion for cleaner display
      return name.replace(/\s+\d{1,2}:\d{2}\s+(AM|PM)\s+-\s+\d{1,2}:\d{2}\s+(AM|PM)$/i, '')
    },
    previousWeek () {
      const newDate = new Date(this.currentDate)
      newDate.setDate(newDate.getDate() - 7)
      this.loadData(newDate)
    },
    nextWeek () {
      const newDate = new Date(this.currentDate)
      newDate.setDate(newDate.getDate() + 7)
      this.loadData(newDate)
    },
    loadData (date) {
      this.isLoading = true

      window.fetch(`${this.calendarUrl}?date=${dateStamp(date)}${this.person ? `&person_id=${this.person.id}` : ''}`)
        .then((response) => response.json())
        .then((data) => {
          for (const d in data) {
            this.datesData[d] = data[d]
          }
          this.currentDate = date
        })
        .catch((err) => alert(err.message))
        .finally(() => {
          this.isLoading = false
        })
    },
    dayColumnClass (day, index) {
      const classes = ['py-2', 'px-1', 'min-h-[150px]']
      const data = this.datesData[day.date] || {}

      if (index === 0) classes.push('rounded-tl-lg', 'rounded-bl-lg')
      if (index === 6) classes.push('rounded-tr-lg', 'rounded-br-lg')

      // Use explicit class names for Tailwind purging
      const bgColors = {
        slate: 'bg-slate-400',
        red: 'bg-red-200',
        orange: 'bg-orange-200',
        yellow: 'bg-yellow-200',
        green: 'bg-green-200',
        blue: 'bg-blue-200',
        sky: 'bg-sky-200'
      }

      if (data.bg && bgColors[data.bg]) {
        classes.push(bgColors[data.bg])
      } else {
        classes.push('bg-white')
      }

      if (day.isToday) {
        classes.push('ring-2', 'ring-sky-600', 'ring-inset')
      }

      return classes.join(' ')
    },
    eventUrl (event) {
      if (event.type === 'Request') {
        return `/requests/${event.id}`
      }
      return `/special_events/${event.id}`
    }
  }
}
</script>
